/**
 * Squigli Studio — a full-revamp name builder.
 *
 * Immersive full-width 3D stage + an app-style dock. Colors: tap a color to
 * paint every letter, or pick one letter first (in the preview or the flat
 * letter tiles) to color just that one; quick-style presets fill the whole
 * strand in one tap. Reuses window.SquigliStrand3D and posts the same
 * line-item properties as the classic builder.
 *
 * Designs can be shared as links: ?name=MARIAM(heart)&colors=pink-purple&hang=vertical
 * (symbols in brackets, "_" for a space, colors by palette name).
 *
 * Words the customer sees come from the section's [data-i18n] JSON (the theme's language files), with English
 * built in as a fallback. What goes into the order (Name, Colors, Orientation) always stays in English.
 */

const S_CHAR_SLUGS = {'&': 'amp', '!': 'excl', '?': 'quest'};
const S_LINK_KEYS = ['name', 'colors', 'hang'];

// Example (ghost) names shown before typing, split by gender so the preview can
// pair them with a fitting symbol. Includes 3-letter names — those always get a
// symbol so the preview reads as a full set (>= min length).
const GHOST_GIRLS = ['LAYLA','MAYA','NOUR','YARA','SARA','RIMA','LEA','JANA','ZEINA','FARAH','DANA','HANA','RITA','MIRA','TALA','LINA','KARMA','MIA','RIM','NAYA'];
const GHOST_BOYS  = ['KARIM','OMAR','ALI','RAMI','ZIAD','SAMI','FADI','JAD','MARWAN','TAREK','NADIM','HADI','ELIE','BILAL','KAMAL','RAYAN','ADAM','SAM','ZEIN','NABIL'];
// Symbol pools by gender (emoji must match keys in the section's data-symbols).
const GHOST_SYM_GIRL = ['🎀','🌸','❤','👑','✨','🐱'];
const GHOST_SYM_BOY  = ['⚽','🏀','⚡','✈','🐶','🎵'];

class NameStudio extends HTMLElement {
  connectedCallback() {
    this.min = parseInt(this.dataset.min, 10) || 4;
    this.max = parseInt(this.dataset.max, 10) || 12;
    this.basePrice = parseFloat(this.dataset.basePrice) || 0;
    this.perChar = parseFloat(this.dataset.perChar) || 0;
    this.currency = this.dataset.currency || '$';
    this.freeDelivery = parseFloat(this.dataset.freeDelivery) || 0;
    this.legendBase = this.dataset.legendBase || '';
    this.symbols = JSON.parse(this.dataset.symbols || '{}');
    try { this.i18n = JSON.parse((this.querySelector('[data-i18n]') || {}).textContent || '{}'); } catch (e) { this.i18n = {}; }
    this.ghostWord = '';
    this.newGhost();

    this.input = this.querySelector('[data-input]');
    this.priceEl = this.querySelector('[data-price]');
    this.priceNote = this.querySelector('[data-price-note]');
    this.hint = this.querySelector('[data-hint]');
    this.submit = this.querySelector('[data-submit]');
    this.tiles = this.querySelector('[data-tiles]');
    this.next = this.querySelector('[data-next]');
    this.brushSwatches = Array.from(this.querySelectorAll('[data-brush]'));
    this.palette = this.brushSwatches.map((b) => b.dataset.brush);

    // One body color per character. `fill` decides the color of letters that
    // don't have one yet: a repeating pattern, one solid color, or (once a
    // single letter was painted) "custom", where colors stay with their letters.
    this.colors = [];
    this.fill = {mode: 'pattern', list: this.palette};
    this.lastChars = [];
    this.orientation = 'horizontal';
    this.selectedIndex = null;
    this.prevCount = 0;
    this.addedName = null;

    this.variants = this.readVariants();
    this.colorNames = {};
    this.colorLabels = {};
    this.brushSwatches.forEach((b) => {
      const hex = b.dataset.brush.toLowerCase();
      this.colorNames[hex] = b.dataset.colorName || b.dataset.brush;
      this.colorLabels[hex] = b.dataset.colorLabel || this.colorNames[hex];
    });
    // URL sync only on the product's own page (not when the studio sits on the homepage)
    this.ownsUrl = !!this.dataset.productUrl && location.pathname.replace(/\/$/, '').endsWith(this.dataset.productUrl);

    this.readLink();
    this.initSound();
    this.init3D();
    this.bind();
    this.render();
    this.startGhostRotation();
  }

  /* ---------- text helpers ---------- */
  // A customer-facing string in the page's language: t('hint_more', '%left% more to go', {left: 2})
  t(key, fallback, vars) {
    let s = this.i18n[key] || fallback;
    Object.keys(vars || {}).forEach((k) => { s = s.split(`%${k}%`).join(String(vars[k])); });
    return s;
  }

  chars() { return Array.from(this.input.value); }

  applyClean() {
    // the clickers only come in A-Z: if Arabic (or other) letters are typed, say so instead of silently dropping them
    if (Array.from(this.input.value).some((ch) => /\p{L}/u.test(ch) && !/[A-Za-z]/.test(ch))) this.latinHit = performance.now();
    const arr = Array.from(this.input.value.toUpperCase()).filter(
      (ch) => /[A-Z0-9 &!?]/.test(ch) || this.symbols[ch]
    );
    while (arr.length && arr[0] === ' ') arr.shift();
    if (arr.length > this.max) this.capHit = performance.now();
    const next = arr.slice(0, this.max).join('');
    if (next !== this.input.value) {
      const pos = Math.min(this.input.selectionStart ?? next.length, next.length);
      this.input.value = next;
      if (document.activeElement === this.input) this.input.setSelectionRange(pos, pos);
    }
  }

  slugFor(ch) {
    if (ch === ' ') return null;
    return this.symbols[ch] ? this.symbols[ch].slug : S_CHAR_SLUGS[ch] || ch;
  }
  legendUrl(ch) {
    if (ch === ' ') return null;
    if (this.symbols[ch]) return this.symbols[ch].url;
    return this.legendBase ? `${this.legendBase}squigli-cap-${S_CHAR_SLUGS[ch] || ch}.png` : null;
  }
  // displayName / nameForColor go into the order (English); uiName / uiColor are what the customer reads
  displayName(ch) { return ch === ' ' ? 'Space' : (this.symbols[ch] ? this.symbols[ch].name : ch); }
  uiName(ch) { return ch === ' ' ? this.t('space', 'Space') : (this.symbols[ch] ? (this.symbols[ch].label || this.symbols[ch].name) : ch); }
  nameForColor(hex) { return this.colorNames[(hex || '').toLowerCase()] || hex; }
  uiColor(hex) { return this.colorLabels[(hex || '').toLowerCase()] || this.nameForColor(hex); }
  trimmed() { const a = this.chars(); while (a.length && a[a.length - 1] === ' ') a.pop(); return a; }
  countChars() { return this.trimmed().length; }
  letterName() { return this.trimmed().filter((c) => !this.symbols[c]).join('').trim(); }
  propName() { return this.trimmed().map((c) => (this.symbols[c] ? `[${this.symbols[c].name}]` : c)).join(''); }
  colorSummary() {
    return this.trimmed().map((c, i) => `${this.displayName(c)}:${this.nameForColor(this.colors[i])}`).join(', ');
  }

  /* ---------- colors ---------- */
  colorAt(i) {
    if (this.fill.mode === 'solid') return this.fill.color;
    const list = this.fill.mode === 'pattern' ? this.fill.list : this.palette;
    return list[i % list.length];
  }

  // Keep colors attached to their letters when the name is edited: unchanged
  // letters before and after the edit keep their color; new ones get the fill.
  syncColors(prev, next) {
    if (this.fill.mode !== 'custom') { this.colors = next.map((_, i) => this.colorAt(i)); return; }
    let p = 0;
    while (p < prev.length && p < next.length && prev[p] === next[p]) p++;
    let s = 0;
    while (s < prev.length - p && s < next.length - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s++;
    const added = next.slice(p, next.length - s).map((_, k) => this.colorAt(p + k));
    this.colors = this.colors.slice(0, p).concat(added, this.colors.slice(prev.length - s));
  }

  hexForName(name) {
    const want = String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!want) return null;
    return this.palette.find((hex) =>
      this.nameForColor(hex).toLowerCase().replace(/[^a-z0-9]/g, '') === want || hex.replace('#', '').toLowerCase() === want
    ) || null;
  }
  linkColorName(hex) { return this.nameForColor(hex).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  // Pick a fresh example name and pair it with a gender-fitting symbol placed
  // before or after it, so the pre-typing preview showcases symbols too. Short
  // (3-letter) names always get at least one symbol to reach the minimum length.
  newGhost() {
    const girl = Math.random() < 0.5;
    const names = girl ? GHOST_GIRLS : GHOST_BOYS;
    const pool = (girl ? GHOST_SYM_GIRL : GHOST_SYM_BOY).filter((s) => this.symbols[s]);
    let name = names[Math.floor(Math.random() * names.length)];
    if (name === this.ghostWord) name = names[(names.indexOf(name) + 1) % names.length];
    this.ghostWord = name;

    let out = name;
    if (pool.length) {
      const sym = pool[Math.floor(Math.random() * pool.length)];
      const before = Math.random() < 0.5;
      out = before ? sym + name : name + sym;
      // occasionally a second symbol on the opposite side, if there is room
      if (pool.length > 1 && Math.random() < 0.25 && Array.from(out).length < this.max) {
        const sym2 = pool[Math.floor(Math.random() * pool.length)];
        out = before ? out + sym2 : sym2 + out;
      }
    }
    // safety net: never render below the minimum length
    while (Array.from(out).length < this.min && pool.length) {
      out += pool[Math.floor(Math.random() * pool.length)];
    }
    this.ghostDisplay = out;
  }

  readVariants() {
    const n = this.querySelector('[data-variants]');
    if (!n) return [];
    try { return JSON.parse(n.textContent); } catch (e) { return []; }
  }
  variantFor(count) {
    return this.variants.find((v) => (v.options || []).some((o) => parseInt(String(o).match(/\d+/), 10) === count));
  }

  /* ---------- share links ---------- */
  // Prefill from ?name=MARIAM(heart)&colors=pink-purple&hang=vertical
  readLink() {
    let q;
    try { q = new URLSearchParams(location.search); } catch (e) { return; }
    const raw = q.get('name');
    if (!raw) return;
    const byName = {};
    Object.keys(this.symbols).forEach((emoji) => (byName[this.symbols[emoji].name.toLowerCase()] = emoji));
    this.input.value = raw.replace(/\(([a-z]+)\)/gi, (m, w) => byName[w.toLowerCase()] || '').replace(/_/g, ' ');
    this.applyClean();
    const chars = this.chars();
    const list = (q.get('colors') || '').split('-').map((c) => this.hexForName(c)).filter(Boolean);
    if (list.length === 1) this.fill = {mode: 'solid', color: list[0]};
    else if (list.length && list.length >= chars.length) this.fill = {mode: 'custom'};
    else if (list.length) this.fill = {mode: 'pattern', list};
    this.colors = chars.map((_, i) => (this.fill.mode === 'custom' ? list[i] : this.colorAt(i)));
    this.lastChars = chars;
    if (q.get('hang') === 'vertical') this.orientation = 'vertical';
  }

  shareUrl() {
    const chars = this.trimmed();
    const name = chars.map((c) => (this.symbols[c] ? `(${this.symbols[c].name.toLowerCase()})` : c === ' ' ? '_' : c)).join('');
    const parts = [`name=${encodeURIComponent(name)}`];
    let colors = '';
    if (this.fill.mode === 'solid') colors = this.linkColorName(this.fill.color);
    else if (this.fill.mode === 'pattern' && this.fill.list !== this.palette) colors = this.fill.list.map((h) => this.linkColorName(h)).join('-');
    else if (this.fill.mode === 'custom') colors = this.colors.slice(0, chars.length).map((h) => this.linkColorName(h)).join('-');
    if (colors) parts.push(`colors=${colors}`);
    if (this.orientation === 'vertical') parts.push('hang=vertical');
    return `${location.origin}${this.dataset.productUrl || location.pathname}?${parts.join('&')}`;
  }
  shareText() {
    const n = this.letterName();
    return n ? this.t('share_text_named', "Here's the %name% clicker set I designed on Squigli", {name: n})
      : this.t('share_text', "Here's the clicker set I designed on Squigli");
  }

  // Mirror the design into the address bar so a reload or a copied URL keeps it.
  syncUrl() {
    if (!this.ownsUrl || !window.history || !history.replaceState) return;
    clearTimeout(this.urlTimer);
    this.urlTimer = setTimeout(() => {
      const keep = new URLSearchParams(location.search);
      S_LINK_KEYS.forEach((k) => keep.delete(k));
      const ours = this.trimmed().length ? this.shareUrl().split('?')[1] : '';
      const search = [ours, keep.toString()].filter(Boolean).join('&');
      const url = location.pathname + (search ? `?${search}` : '') + location.hash;
      if (url !== location.pathname + location.search + location.hash) {
        try { history.replaceState(history.state, '', url); } catch (e) {}
      }
    }, 300);
  }

  copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    ta.remove();
    return Promise.resolve();
  }

  initShare() {
    this.shareBtn = this.querySelector('[data-share]');
    this.shareMenu = this.querySelector('[data-share-menu]');
    if (!this.shareBtn || !this.shareMenu) return;
    const wa = this.querySelector('[data-share-wa]');
    const copy = this.querySelector('[data-share-copy]');
    const setMenu = (open) => {
      this.shareMenu.hidden = !open;
      this.shareBtn.setAttribute('aria-expanded', String(open));
      if (open && wa) wa.href = `https://wa.me/?text=${encodeURIComponent(`${this.shareText()}: ${this.shareUrl()}`)}`;
    };
    this.closeShare = () => setMenu(false);
    this.shareBtn.addEventListener('click', () => {
      // Phones: the native share sheet (WhatsApp, Instagram, Messages...). Elsewhere: a small menu.
      const touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (navigator.share && touch) {
        navigator.share({title: 'Squigli', text: this.shareText(), url: this.shareUrl()}).catch(() => {});
        return;
      }
      setMenu(this.shareMenu.hidden);
    });
    if (wa) wa.addEventListener('click', () => setTimeout(() => setMenu(false), 0));
    if (copy) copy.addEventListener('click', () => {
      this.copyText(this.shareUrl()).then(() => {
        copy.textContent = this.t('link_copied', 'Link copied ✓');
        setTimeout(() => { copy.textContent = this.t('copy_link', 'Copy link'); setMenu(false); }, 1400);
      });
    });
    document.addEventListener('click', (e) => {
      if (!this.shareMenu.hidden && !e.target.closest('.studio__share-wrap')) setMenu(false);
    });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.shareMenu.hidden) { setMenu(false); this.shareBtn.focus(); } });
  }

  /* ---------- sound (synth click; recording optional) ---------- */
  initSound() {
    this.ctx = null;
    this.click = () => {
      try {
        if (!this.ctx) { const C = window.AudioContext || window.webkitAudioContext; this.ctx = new C(); }
        if (this.ctx.state === 'suspended') this.ctx.resume();
        const t = this.ctx.currentTime, len = Math.floor(this.ctx.sampleRate * 0.03);
        const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate), d = buf.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 7);
        const src = this.ctx.createBufferSource(); src.buffer = buf;
        const hp = this.ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 1500;
        const g = this.ctx.createGain(); g.gain.setValueAtTime(0.45, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
        src.connect(hp).connect(g).connect(this.ctx.destination); src.start(t);
      } catch (e) {}
    };
  }

  /* ---------- 3D ---------- */
  init3D() {
    const lib = window.SquigliStrand3D;
    if (!lib || !this.dataset.meshUrl || this.strand) return;   // the engine loads after the page: the section calls init3D again then
    if (!lib.supported()) { this.initFlat(); return; }          // no WebGL on this device: flat preview
    const stage = this.querySelector('[data-stage]');
    const canvas = document.createElement('canvas');
    canvas.className = 'studio__canvas';
    stage.insertBefore(canvas, stage.firstChild);
    const svgLegends = {};
    Object.values(this.symbols).forEach((s) => { if (s.url) svgLegends[s.slug] = s.url; });
    try {
      this.strand = lib.create({
        canvas, meshUrl: this.dataset.meshUrl, svgLegends, maxChars: this.max, zoom: 1.18,
        onReady: () => this.render(),
        onPick: (i) => this.onCapTap(i),
        onMiss: () => { if (this.selectedIndex != null) { this.selectedIndex = null; this.render(); } },
      });
    } catch (e) { canvas.remove(); this.strand = null; this.initFlat(); return; }
    const fit = () => {
      const r = stage.getBoundingClientRect();
      // Phones: frame names closer so letters are big enough to see and tap; wide screens keep the roomier framing.
      const narrow = r.width < 750;
      this.strand.zoom = narrow ? 1.04 : 1.18;
      this.strand.fitPad = narrow ? 1.12 : 1.43;
      this.strand.resize(r.width, Math.max(r.height, 200));
    };
    new ResizeObserver(fit).observe(stage);
    fit();
  }

  /* ---------- flat preview: when this device can't run the 3D one ---------- */
  // A flat strand of keycaps (the same letter faces as the 3D caps) on a cord, sideways or top to bottom,
  // with a note saying the 3D preview isn't working here. Tapping a cap picks it, like on the 3D strand.
  initFlat() {
    if (this.flat) return;
    const stage = this.querySelector('[data-stage]');
    this.flat = document.createElement('div');
    this.flat.className = 'studio__flat';
    this.flat.setAttribute('dir', 'ltr');
    this.flat.innerHTML = '<div class="studio__flat-row" data-flat-row></div>';
    stage.insertBefore(this.flat, stage.firstChild);
    const note = document.createElement('p');
    note.className = 'studio__no3d';
    const ta = document.createElement('textarea');   // Shopify's t filter HTML-escapes the apostrophes
    ta.innerHTML = this.t('no3d', "The 3D preview isn't working on this device, so here's a flat preview instead. You can still build and order as usual.");
    note.textContent = ta.value;
    stage.appendChild(note);
    this.classList.add('is-flat');
    new ResizeObserver(() => this.renderFlat(this.chars())).observe(this.flat);
  }

  renderFlat(chars) {
    if (!this.flat) return;
    const row = this.flat.firstChild, ghost = !chars.length, list = ghost ? Array.from(this.ghostDisplay || '') : chars;
    const vertical = this.orientation === 'vertical';
    this.flat.classList.toggle('is-vertical', vertical);
    this.flat.classList.toggle('is-ghost', ghost);
    while (row.children.length > list.length) row.lastChild.remove();
    while (row.children.length < list.length) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'studio__flatcap';
      b.innerHTML = '<span class="studio__flatcap-legend" aria-hidden="true"></span>';
      b.addEventListener('click', () => this.onCapTap(Array.prototype.indexOf.call(row.children, b)));
      row.appendChild(b);
    }
    list.forEach((ch, i) => {
      const b = row.children[i], url = this.legendUrl(ch), color = ghost ? this.colorAt(i) : this.colors[i];
      b.style.setProperty('--cap', color);
      b.firstChild.style.setProperty('--legend', url ? `url("${url}")` : 'none');
      b.classList.toggle('is-space', ch === ' ');
      b.classList.toggle('is-symbol', !!this.symbols[ch]);
      b.classList.toggle('is-selected', !ghost && this.selectedIndex === i);
      b.tabIndex = ghost ? -1 : 0;
      b.setAttribute('aria-label', `${this.uiName(ch)}, ${this.uiColor(color)}`);
    });
    // as big as fits the stage
    const r = this.flat.getBoundingClientRect(), n = Math.max(1, list.length), gap = 8;
    const size = Math.max(26, Math.min(vertical ? 66 : 96, ((vertical ? r.height : r.width) - gap * (n - 1)) / n));
    this.flat.style.setProperty('--size', `${Math.floor(size)}px`);
  }

  /* tapping a cap or tile selects it, so the next color paints only that one (tap again to deselect) */
  onCapTap(i) {
    if (!this.chars().length) return;
    this.selectedIndex = (this.selectedIndex === i) ? null : i;
    this.click();
    this.render();
  }

  /* flat letter tiles under the colors label: same selection as tapping the 3D caps */
  renderTiles(chars) {
    const box = this.tiles;
    if (!box) return;
    box.hidden = !chars.length;
    while (box.children.length > chars.length) box.lastChild.remove();
    while (box.children.length < chars.length) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'studio__tile';
      b.innerHTML = '<span class="studio__tile-legend" aria-hidden="true"></span>';
      b.addEventListener('click', () => this.onCapTap(Array.prototype.indexOf.call(box.children, b)));
      box.appendChild(b);
    }
    chars.forEach((ch, i) => {
      const b = box.children[i];
      const url = this.legendUrl(ch);
      const on = this.selectedIndex === i;
      b.style.setProperty('--tile', this.colors[i]);
      b.firstChild.style.setProperty('--legend', url ? `url("${url}")` : 'none');
      b.classList.toggle('is-blank', !url);
      b.classList.toggle('is-symbol', !!this.symbols[ch]);
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', String(on));
      b.setAttribute('aria-label', `${this.uiName(ch)}, ${this.uiColor(this.colors[i])}`);
    });
  }

  /* update the colors label + floating coach-mark based on current selection */
  updateColorHints() {
    const selecting = this.selectedIndex != null;
    // "is-selecting" lets the CSS draw attention to the palette while a letter is picked
    this.classList.toggle('is-selecting', selecting);

    const hint = this.querySelector('[data-colors-hint]');
    if (hint) {
      if (selecting) {
        const ch = this.chars()[this.selectedIndex];
        hint.textContent = this.t('colors_hint_selected', 'Now tap a color for “%letter%”', {letter: ch ? this.uiName(ch) : this.t('this_letter', 'this letter')});
        hint.classList.add('is-active');
      } else {
        hint.textContent = this.chars().length ? this.t('colors_hint_more', 'Tap a color to paint every letter, or tap one letter first')
          : this.t('colors_hint', 'Tap a color to paint every letter');
        hint.classList.remove('is-active');
      }
    }
    const coach = this.querySelector('[data-coach]');
    if (coach) {
      let learned = false;
      try { learned = localStorage.getItem('squigli_studio_coach') === '1'; } catch (e) {}
      // Keep coaching until they successfully color a single letter once.
      coach.hidden = learned || selecting || this.chars().length < this.min;
    }
  }

  /* a color chip: paint the selected letter if one is picked, otherwise the whole strand */
  applyColor(hex) {
    this.setBrush(hex);
    if (this.selectedIndex != null && this.selectedIndex < this.chars().length) {
      this.colors[this.selectedIndex] = hex;   // color just the selected letter
      this.fill = {mode: 'custom'};
      this.selectedIndex = null;                // back to whole-strand mode
      try { localStorage.setItem('squigli_studio_coach', '1'); } catch (e) {}  // learned it — stop coaching
    } else {
      this.fill = {mode: 'solid', color: hex};
      this.colors = this.colors.map(() => hex);  // paint the whole strand
    }
    this.click();
    this.render();
  }

  setBrush(hex) {
    this.brushSwatches.forEach((b) => b.classList.toggle('is-active', !!hex && b.dataset.brush === hex));
  }

  /* ---------- guided tour (opened from the "?" button) ---------- */
  initTour() {
    this.tour = this.querySelector('[data-tour]');
    if (!this.tour) return;
    this.tourPop = this.querySelector('[data-tour-pop]');
    this.allTourSteps = [
      { sel: '[data-input]',   emoji: '✏️', title: this.t('tour_type_title', 'Type a name'), body: this.t('tour_type_body', 'Type any name or word here, then tap Next.') },
      { sel: '.studio__chips', emoji: '🎨', title: this.t('tour_color_title', 'Pick a color'), body: this.t('tour_color_body', 'Tap a color to paint the whole name, or try a quick style like Rainbow.') },
      { sel: '[data-tiles]',   emoji: '🌈', title: this.t('tour_letter_title', 'Color one letter'), body: this.t('tour_letter_body', 'We picked your first letter. Tap a color to change just that one, or tap any other letter first.'), selectFirst: true, needsName: true },
    ];
    this.querySelectorAll('[data-tour-skip]').forEach((el) => el.addEventListener('click', () => this.endTour()));
    this.querySelector('[data-tour-next]').addEventListener('click', () => this.tourNext());
    this.querySelector('[data-tour-prev]').addEventListener('click', () => this.tourPrev());
    const help = this.querySelector('[data-help]');
    if (help) help.addEventListener('click', () => this.startTour());
    window.addEventListener('resize', () => { if (this.tour && !this.tour.hidden) this.positionTour(); });
  }

  startTour() {
    this.tourSteps = this.allTourSteps.filter((s) => !s.needsName || this.chars().length);
    this.tourIdx = 0; this.tour.hidden = false; this.showTourStep();
  }

  endTour() {
    if (!this.tour) return;
    this.tour.hidden = true;
    this.querySelectorAll('.studio-tour-target').forEach((e) => e.classList.remove('studio-tour-target'));
    this.selectedIndex = null; this.render();
  }

  tourNext() { if (this.tourIdx >= this.tourSteps.length - 1) this.endTour(); else { this.tourIdx++; this.showTourStep(); } }
  tourPrev() { if (this.tourIdx > 0) { this.tourIdx--; this.showTourStep(); } }

  showTourStep() {
    const step = this.tourSteps[this.tourIdx];
    // per-letter step: actually select the first letter so its tile and cap lift
    this.selectedIndex = (step.selectFirst && this.chars().length) ? 0 : null;
    this.render();
    this.querySelectorAll('.studio-tour-target').forEach((e) => e.classList.remove('studio-tour-target'));
    const target = this.querySelector(step.sel);
    if (target) target.classList.add('studio-tour-target');
    this.tourTarget = target;
    this.querySelector('[data-tour-emoji]').textContent = step.emoji;
    this.querySelector('[data-tour-title]').textContent = `${this.tourIdx + 1}. ${step.title}`;
    this.querySelector('[data-tour-body]').textContent = step.body;
    const dots = this.querySelector('[data-tour-dots]');
    dots.innerHTML = this.tourSteps.map((_, i) => `<span class="studio__tour-dot${i === this.tourIdx ? ' is-on' : ''}"></span>`).join('');
    const prev = this.querySelector('[data-tour-prev]');
    const next = this.querySelector('[data-tour-next]');
    prev.style.visibility = this.tourIdx === 0 ? 'hidden' : 'visible';
    next.textContent = this.tourIdx === this.tourSteps.length - 1 ? this.t('lets_go', "Let's go! 🎉") : this.t('next', 'Next');
    this.positionTour();
    if (target && target.scrollIntoView) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setTimeout(() => this.positionTour(), 400);
  }

  positionTour() {
    const target = this.tourTarget, pop = this.tourPop;
    if (!target || !pop) return;
    const r = target.getBoundingClientRect();
    const pw = pop.offsetWidth, ph = pop.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight, gap = 14;
    let left = r.left + r.width / 2 - pw / 2;
    left = Math.max(gap, Math.min(left, vw - pw - gap));
    let top;
    if (r.bottom + gap + ph <= vh) top = r.bottom + gap;          // below target
    else if (r.top - gap - ph >= 0) top = r.top - gap - ph;       // above target
    else top = Math.max(gap, (vh - ph) / 2);                      // fallback: centre
    pop.style.left = left + 'px';
    pop.style.top = top + 'px';
  }

  /* ---------- sticky checkout bar (phones) ---------- */
  // The bar sticks to the bottom of the screen via CSS; while it does, lift the
  // floating WhatsApp button above it so it doesn't cover "Add to cart".
  initStickyBar() {
    this.bar = this.querySelector('[data-bar]');
    if (!this.bar) return;
    const root = document.documentElement;
    const check = () => {
      this.barRaf = 0;
      const r = this.bar.getBoundingClientRect();
      const stuck = getComputedStyle(this.bar).position === 'sticky' && this.classList.contains('is-ready') &&
        r.bottom >= window.innerHeight - 2 && r.top < window.innerHeight;
      root.classList.toggle('squigli-bar-up', stuck);
      if (stuck) root.style.setProperty('--squigli-bar-h', `${Math.round(r.height)}px`);
    };
    this.checkBar = () => { if (!this.barRaf) this.barRaf = requestAnimationFrame(check); };
    window.addEventListener('scroll', this.checkBar, {passive: true});
    window.addEventListener('resize', this.checkBar);
  }

  /* ---------- after adding: "add another name" + free delivery nudge ---------- */
  onAdded() {
    if (!this.next) return;
    this.addedName = this.trimmed().join('');
    const n = this.letterName();
    this.querySelector('[data-next-added]').textContent = n ? this.t('in_cart_named', '%name% is in your cart ✓', {name: n}) : this.t('in_cart', 'Added to your cart ✓');
    const fd = this.querySelector('[data-next-fd]');
    fd.textContent = '';
    this.next.hidden = false;
    if (this.checkBar) this.checkBar();
    if (!this.freeDelivery) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}})
      .then((r) => r.json())
      .then((cart) => {
        const left = Math.round(this.freeDelivery * 100) - cart.total_price;
        fd.textContent = left > 0 ? this.t('fd_more', 'Add %amount% more for free delivery.', {amount: this.money(left)}) : this.t('fd_unlocked', 'Free delivery unlocked 🎉');
        if (this.checkBar) this.checkBar();
      })
      .catch(() => {});
  }

  // Start a fresh name, keeping the current colors as the pattern for it.
  startAnother() {
    if (this.fill.mode === 'custom' && this.colors.length) this.fill = {mode: 'pattern', list: this.colors.slice()};
    this.setBrush(this.fill.mode === 'solid' ? this.fill.color : null);
    this.input.value = '';
    const note = this.querySelector('[data-note]');
    if (note) note.value = '';
    if (this.next) this.next.hidden = true;
    this.addedName = null;
    this.selectedIndex = null;
    this.render();
    this.input.focus({ preventScroll: true });
    const stage = this.querySelector('[data-stage]');
    if (stage) stage.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /* ---------- events ---------- */
  bind() {
    this.input.addEventListener('input', () => { this.applyClean(); this.render(); });

    // color chips: paint the selected letter (if any) or the whole strand
    this.brushSwatches.forEach((b) => b.addEventListener('click', () => this.applyColor(b.dataset.brush)));

    this.initTour();
    this.initShare();
    this.initStickyBar();

    // presets
    this.querySelectorAll('[data-preset]').forEach((btn) => btn.addEventListener('click', () => {
      this.fill = {mode: 'pattern', list: btn.dataset.colors.split(',').map((c) => c.trim())};
      this.colors = this.colors.map((_, i) => this.colorAt(i));
      this.setBrush(null);
      this.click(); this.render();
    }));

    // surprise
    const surprise = this.querySelector('[data-surprise]');
    if (surprise) surprise.addEventListener('click', () => {
      if (!this.colors.length) return;
      this.fill = {mode: 'custom'};
      this.colors = this.colors.map(() => this.palette[Math.floor(Math.random() * this.palette.length)]);
      this.setBrush(null);
      this.click(); this.render();
    });

    // orientation
    this.querySelectorAll('[data-orient]').forEach((btn) => btn.addEventListener('click', () => {
      this.orientation = btn.dataset.orient;
      this.click(); this.render();
    }));

    // symbols
    this.querySelectorAll('[data-insert]').forEach((btn) => btn.addEventListener('click', () => {
      if (this.chars().length >= this.max) return;
      const sym = btn.dataset.insert, pos = this.input.selectionStart ?? this.input.value.length;
      this.input.value = this.input.value.slice(0, pos) + sym + this.input.value.slice(pos);
      this.input.focus({ preventScroll: true }); this.input.setSelectionRange(pos + sym.length, pos + sym.length);
      this.click(); this.render();
    }));

    // "add another name": the builder's own button, and the link in the cart drawer
    const another = this.querySelector('[data-another]');
    if (another) another.addEventListener('click', () => this.startAnother());
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('[data-squigli-another]');
      if (!a || e.defaultPrevented || !this.dataset.productUrl) return;
      if ((a.getAttribute('href') || '').split('?')[0] !== this.dataset.productUrl) return;
      e.preventDefault();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
      this.startAnother();
    });

    // add to cart
    const form = this.querySelector('[data-cart-form]');
    if (form) form.addEventListener('submit', (e) => {
      this.querySelector('[data-prop-name]').value = this.propName();
      this.querySelector('[data-prop-colors]').value = this.colorSummary();
      this.querySelector('[data-prop-orient]').value = this.orientation === 'vertical' ? 'Vertical' : 'Horizontal';
      const noteEl = this.querySelector('[data-note]');
      const noteVal = noteEl ? noteEl.value.trim() : '';
      const noteInput = this.querySelector('[data-prop-note]');
      if (noteInput) noteInput.value = noteVal;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return;
      e.preventDefault();
      this.submit.disabled = true; this.submit.classList.add('is-adding');
      const fd = new FormData(form);
      if (!noteVal) fd.delete('properties[Note]');
      fd.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
      fd.append('sections_url', window.location.pathname);
      fetch('/cart/add', {method: 'POST', body: fd, headers: {Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest'}})
        .then((r) => r.json())
        .then((res) => {
          if (res.status) { this.hint.textContent = res.description || this.t('could_not_add', 'Could not add.'); return; }
          this.click(); drawer.classList.remove('is-empty'); drawer.renderContents(res);
          this.onAdded();
        })
        .catch(() => form.submit())
        .finally(() => { this.submit.classList.remove('is-adding'); this.updateSummary(); });
    });
  }

  startGhostRotation() {
    const sync = () => { if (this.input) this.input.placeholder = this.t('example', 'e.g. %name%', {name: this.ghostWord}); };
    sync();
    setInterval(() => {
      if (this.chars().length || document.hidden) return;
      this.newGhost(); sync(); this.render();
    }, 3200);
  }

  /* ---------- render ---------- */
  render() {
    const chars = this.chars();
    if (chars.join('') !== this.lastChars.join('')) {
      this.syncColors(this.lastChars, chars);
      this.lastChars = chars;
      this.selectedIndex = null;
      if (this.next && !this.next.hidden && this.trimmed().join('') !== this.addedName) this.next.hidden = true;
    }
    if (this.selectedIndex != null && this.selectedIndex >= chars.length) this.selectedIndex = null;

    const vertical = this.orientation === 'vertical';
    this.querySelectorAll('[data-orient]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.orient === this.orientation)));
    if (this.strand) {
      if (chars.length === 0) {
        const g = Array.from(this.ghostDisplay).map((ch, i) => ({slug: this.slugFor(ch), color: this.colorAt(i)}));
        this.strand.update(g, null, true, vertical);
      } else {
        this.strand.update(chars.map((ch, i) => ({slug: this.slugFor(ch), color: this.colors[i]})), this.selectedIndex, false, vertical);
      }
      if (chars.length > this.prevCount) this.click();
      this.prevCount = chars.length;
    } else if (this.flat) {
      this.renderFlat(chars);
      if (chars.length > this.prevCount) this.click();
      this.prevCount = chars.length;
    }
    this.classList.toggle('is-empty', chars.length === 0);
    if (this.shareBtn) {
      this.shareBtn.hidden = !this.trimmed().length;
      if (this.shareBtn.hidden && this.closeShare) this.closeShare();
    }
    this.renderTiles(chars);
    this.updateSummary();
    this.updateColorHints();
    this.syncUrl();
  }

  updateSummary() {
    const count = this.countChars();
    const valid = count >= this.min && count <= this.max;
    if (this.selectedIndex != null) { this.hint.textContent = this.t('hint_selected', '🎨 Now tap a color to paint this clicker'); }
    else if (this.latinHit && performance.now() - this.latinHit < 2500) { this.hint.textContent = this.t('hint_latin', 'Please use English letters (A–Z).'); }
    else if (this.capHit && performance.now() - this.capHit < 1800) { this.hint.textContent = this.t('hint_cap', 'Up to %max% clickers.', {max: this.max}); }
    else if (!count) this.hint.textContent = '';
    else if (count < this.min) this.hint.textContent = this.t('hint_more', '%left% more to go (min %min%)', {left: this.min - count, min: this.min});
    else this.hint.textContent = this.t('hint_count', '%count% clickers', {count});

    const variant = valid ? this.variantFor(count) : null;
    if (variant) {
      this.priceEl.textContent = this.money(variant.price);
      this.priceNote.textContent = variant.available ? '' : this.t('unavailable', 'Unavailable');
    } else if (valid) {
      this.priceEl.textContent = this.money(Math.round((this.basePrice + this.perChar * count) * 100));
      this.priceNote.textContent = '';
    } else {
      const from = this.variantFor(this.min);
      this.priceEl.textContent = this.money(from ? from.price : Math.round((this.basePrice + this.perChar * this.min) * 100));
      this.priceNote.textContent = this.t('from_letters', 'from %min% letters', {min: this.min});
    }

    const ok = valid && (!this.variants.length || (variant && variant.available));
    this.classList.toggle('is-ready', !!ok);
    if (this.submit) {
      this.submit.disabled = !ok;
      const idf = this.querySelector('[data-variant-id]');
      if (idf && variant) idf.value = variant.id;
    }
    if (this.checkBar) this.checkBar();
  }

  // Whole amounts without decimals ($13), otherwise two decimals ($13.50)
  money(cents) {
    const v = cents / 100;
    return `${this.currency}${Number.isInteger(v) ? v : v.toFixed(2)}`;
  }
}

if (!customElements.get('name-studio')) customElements.define('name-studio', NameStudio);
